/**
 * Telegram Mini App SDK integration
 * Uses @telegram-apps/sdk-react for type-safe Telegram WebApp access
 */

import {
  init,
  postEvent,
  backButton,
  initData,
  miniApp,
  viewport,
  themeParams,
} from '@telegram-apps/sdk-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

// Track initialization state
let sdkInitialized = false;

/**
 * Initialize the Telegram SDK. Call once on app mount.
 * Safe to call multiple times - will only init once.
 */
export function initTelegramSDK(): boolean {
  if (sdkInitialized) return true;

  try {
    // Initialize the SDK with all available components
    init();

    // Mount components that need it
    if (backButton.mount.isAvailable()) {
      backButton.mount();
    }

    // Expand viewport if possible
    if (viewport.mount.isAvailable()) {
      viewport.mount();
      if (viewport.expand.isAvailable()) {
        viewport.expand();
      }
    }

    // Request fullscreen on mobile, then bind safe area CSS vars
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      try {
        postEvent('web_app_request_fullscreen');
      } catch {
        // Fullscreen not supported on older clients
      }
    }

    // Set safe area padding via CSS custom property
    // Uses Telegram WebApp API (safeAreaInset + contentSafeAreaInset)
    const updateSafeArea = () => {
      const wa = (window as any).Telegram?.WebApp;
      const saTop = wa?.safeAreaInset?.top ?? 0;
      const csaTop = wa?.contentSafeAreaInset?.top ?? 0;
      const total = saTop + csaTop;
      document.documentElement.style.setProperty('--safe-top', `${total > 0 ? total : (isMobile ? 54 : 0)}px`);
      const saBottom = wa?.safeAreaInset?.bottom ?? 0;
      document.documentElement.style.setProperty('--safe-bottom', `${saBottom}px`);
    };
    updateSafeArea();
    // Re-check after fullscreen settles
    setTimeout(updateSafeArea, 300);
    setTimeout(updateSafeArea, 1000);
    // Listen for safe area changes
    try {
      (window as any).Telegram?.WebApp?.onEvent?.('safeAreaChanged', updateSafeArea);
      (window as any).Telegram?.WebApp?.onEvent?.('contentSafeAreaChanged', updateSafeArea);
    } catch { /* ignore */ }

    // Also bind SDK CSS vars for future use
    try {
      if (viewport.bindCssVars.isAvailable()) {
        viewport.bindCssVars();
      }
    } catch { /* ignore */ }

    // Mount mini app for ready signal
    if (miniApp.mount.isAvailable()) {
      miniApp.mount();
    }

    // Mount theme params
    if (themeParams.mount.isAvailable()) {
      themeParams.mount();
    }

    // Signal ready to Telegram
    postEvent('web_app_ready');

    sdkInitialized = true;
    return true;
  } catch (error) {
    console.warn('Failed to initialize Telegram SDK:', error);
    return false;
  }
}

/**
 * Check if we're running inside Telegram
 */
export function isInsideTelegram(): boolean {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp;
}

/**
 * Get the raw initData string for API authentication
 */
export function getInitDataRaw(): string | undefined {
  // Try SDK first
  try {
    const raw = initData.raw();
    if (raw) return raw;
  } catch {
    // SDK not ready
  }
  // Fallback to window.Telegram
  const webAppData = window.Telegram?.WebApp?.initData;
  if (webAppData) return webAppData;
  return undefined;
}

/**
 * Hook to get initData for API authentication
 */
export function useInitData() {
  const raw = useMemo(() => getInitDataRaw(), []);

  const state = useMemo(() => {
    try {
      return initData.state();
    } catch {
      return null;
    }
  }, []);

  return {
    raw,
    state,
    isAuthenticated: !!raw,
  };
}

/**
 * Hook to get the current Telegram user
 */
export function useTelegramUser() {
  const { state } = useInitData();

  const user = useMemo(() => {
    if (state?.user) {
      return state.user;
    }
    // Fallback to window.Telegram
    return window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
  }, [state]);

  return user;
}

/**
 * Hook to get the startapp parameter (for deep links)
 */
export function useStartParam(): string | undefined {
  return useMemo(() => {
    try {
      const state = initData.state();
      return state?.startParam;
    } catch {
      return window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    }
  }, []);
}

/**
 * Hook to control the Telegram BackButton
 */
export function useBackButton(onBack?: () => void) {
  const [isVisible, setIsVisible] = useState(false);

  const show = useCallback(() => {
    try {
      if (backButton.show.isAvailable()) {
        backButton.show();
        setIsVisible(true);
      }
    } catch {
      // Not available outside Telegram
    }
  }, []);

  const hide = useCallback(() => {
    try {
      if (backButton.hide.isAvailable()) {
        backButton.hide();
        setIsVisible(false);
      }
    } catch {
      // Not available outside Telegram
    }
  }, []);

  useEffect(() => {
    if (!onBack) return;

    try {
      if (backButton.onClick.isAvailable()) {
        backButton.onClick(onBack);
        return () => {
          backButton.offClick(onBack);
        };
      }
    } catch {
      // Not available outside Telegram
    }
  }, [onBack]);

  return { show, hide, isVisible };
}

/**
 * Trigger haptic feedback
 */
export function hapticFeedback(
  type: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light'
) {
  try {
    postEvent('web_app_trigger_haptic_feedback', {
      type: 'impact',
      impact_style: type,
    });
  } catch {
    // Haptics not available
  }
}

/**
 * Trigger notification haptic
 */
export function hapticNotification(type: 'success' | 'warning' | 'error') {
  try {
    postEvent('web_app_trigger_haptic_feedback', {
      type: 'notification',
      notification_type: type,
    });
  } catch {
    // Haptics not available
  }
}
